import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { MetadataLoadingStage, UserContextService } from '../services/user-context.service';
import { SharedService } from '../services/shared-service';
import { filter, switchMap, map } from 'rxjs/operators';
import { ORGANIZATION_ROLE_ROUTE_ACCESS } from '../route-access.config';
import { LogError } from '@memberjunction/core';
import { ADMIN_CONTACT_ROLE_NAME, OWNER_CONTACT_ROLE_NAME } from '../util';

export const RoleAccessGuard: CanActivateFn = (route, state) => {
  // Inject the required services
  const userContextService = inject(UserContextService);
  const sharedService = inject(SharedService);
  const helper = new RoleAccessGuardHelper(userContextService, sharedService);

  return sharedService.appInitialized$.pipe(
    filter((initialized) => initialized), // Wait for appInitialized$ to emit true
    switchMap(() => userContextService.MetadataLoadingStage$), // Check if contact role data is loaded
    filter((loaded) => loaded >= MetadataLoadingStage.ContactRoleLoaded), // Ensure Contact role data is fully loaded
    map(() => helper.checkAccess(route)) // Check access based on the route and user's contact role
  );
};

class RoleAccessGuardHelper {
  constructor(
    private userContextService: UserContextService,
    private sharedService: SharedService
  ) {}

  public checkAccess(route: ActivatedRouteSnapshot): boolean {
    // Use the routeConfig.path to match our configuration.
    const routePath = route.routeConfig?.path;
    if (!routePath) {
      // If no specific path is defined, allow access
      return true;
    }
  
    // Look up the route's access configuration
    const accessDef = ORGANIZATION_ROLE_ROUTE_ACCESS.find(item => item.path === routePath);
    if (!accessDef) {
      // If there's no matching configuration, allow access by default
      return true;
    }
  
    // Determine the user's roles from your organization service.
    // (You might want to adjust these properties based on your actual implementation.)
    const userRole = this.userContextService.ContactRole.Name.trim().toLowerCase();
    const isOwner = (userRole === OWNER_CONTACT_ROLE_NAME.trim().toLowerCase());
    const isAdmin = (
      userRole === OWNER_CONTACT_ROLE_NAME.trim().toLocaleLowerCase() || 
      userRole === ADMIN_CONTACT_ROLE_NAME.trim().toLocaleLowerCase()
    ); // assuming owners are also admins
    const isInternalSuperAdmin = this.userContextService.IsContactInternalSuperAdmin;
  
    if (accessDef.requiresInternalSuperAdmin && !isInternalSuperAdmin) {
      const message = `Access denied. Internal Super Admin role is required to access ${routePath}.`;
      LogError(message);
      this.sharedService.NoAccessMessage = message;
      this.sharedService.AccessDenied = true;
      return false;
    }
  
    // Check against the requirements
    if (accessDef.requiresOwner && !isOwner) {
      const message = `Access denied. Owner role is required to access ${routePath}.`;
      LogError(message);
      this.sharedService.NoAccessMessage = message;
      this.sharedService.AccessDenied = true;
      return false;
    }
    if (accessDef.requiresAdmin && !isAdmin) {
      const message = `Access denied. Admin role is required to access ${routePath}.`;
      LogError(message);
      this.sharedService.NoAccessMessage = message;
      this.sharedService.AccessDenied = true;
      return false;
    }
  
    return true;
  }
}
